import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { authApi } from '../services/api';
import type { UserInfo, RoleSummary } from '../types/auth';

interface AuthState {
  // 状态
  user: UserInfo | null;
  permissions: string[];
  accessToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  tokenExpiry: number | null;
  isHydrated: boolean;
  
  // Actions
  login: (username: string, password: string, captcha: string, captchaId: string) => Promise<void>;
  logout: () => Promise<void>;
  fetchCurrentUser: () => Promise<void>;
  setAccessToken: (token: string | null, expiresAt?: number) => void;
  hasPermission: (permission: string | string[]) => boolean;
  checkTokenExpiry: () => boolean;
  setHydrated: (value: boolean) => void;
}

// Token 有效期默认值（毫秒）
const DEFAULT_TOKEN_EXPIRY = 24 * 60 * 60 * 1000; // 24小时

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      permissions: [],
      accessToken: null,
      isAuthenticated: false,
      isLoading: false,
      tokenExpiry: null,
      isHydrated: false,

      login: async (username: string, password: string, captcha: string, captchaId: string) => {
        set({ isLoading: true });
        try {
          const response = await authApi.login({
            username,
            password,
            captcha,
            captchaId,
          });
          
          const { user, permissions, accessToken, expiresAt } = response;
          const expiryTime = expiresAt || Date.now() + DEFAULT_TOKEN_EXPIRY;
          
          localStorage.setItem('accessToken', accessToken);
          localStorage.setItem('tokenExpiry', expiryTime.toString());
          
          set({
            user,
            permissions,
            accessToken,
            isAuthenticated: true,
            isLoading: false,
            tokenExpiry: expiryTime,
          });
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },

      logout: async () => {
        try {
          // 可以调用后端登出接口
        } finally {
          localStorage.removeItem('accessToken');
          localStorage.removeItem('tokenExpiry');
          localStorage.removeItem('auth-storage');
          
          set({
            user: null,
            permissions: [],
            accessToken: null,
            isAuthenticated: false,
            isLoading: false,
            tokenExpiry: null,
          });
        }
      },

      fetchCurrentUser: async () => {
        try {
          const user = await authApi.getCurrentUser();
          set({ user });
        } catch (error) {
          console.error('获取用户信息失败:', error);
        }
      },

      setAccessToken: (token: string | null, expiresAt?: number) => {
        if (token) {
          const expiryTime = expiresAt || Date.now() + DEFAULT_TOKEN_EXPIRY;
          localStorage.setItem('accessToken', token);
          localStorage.setItem('tokenExpiry', expiryTime.toString());
          set({ 
            accessToken: token, 
            isAuthenticated: true, 
            tokenExpiry: expiryTime 
          });
        } else {
          localStorage.removeItem('accessToken');
          localStorage.removeItem('tokenExpiry');
          set({ 
            accessToken: null, 
            isAuthenticated: false, 
            tokenExpiry: null 
          });
        }
      },

      hasPermission: (permission: string | string[]) => {
        const { permissions } = get();
        if (permissions.includes('*:*')) return true;
        
        if (Array.isArray(permission)) {
          return permission.some(p => permissions.includes(p));
        }
        return permissions.includes(permission);
      },
      
      checkTokenExpiry: () => {
        const { tokenExpiry, accessToken } = get();
        
        if (!accessToken || !tokenExpiry) {
          return false;
        }
        
        const now = Date.now();
        const isExpired = now >= tokenExpiry;
        
        if (isExpired) {
          localStorage.removeItem('accessToken');
          localStorage.removeItem('tokenExpiry');
          set({
            accessToken: null,
            isAuthenticated: false,
            tokenExpiry: null,
          });
          return false;
        }
        
        return true;
      },

      setHydrated: (value: boolean) => {
        set({ isHydrated: value });
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({ 
        user: state.user, 
        permissions: state.permissions,
        accessToken: state.accessToken,
        isAuthenticated: state.isAuthenticated,
        tokenExpiry: state.tokenExpiry,
      }),
      onRehydrateStorage: () => (state) => {
        if (state) {
          state.setHydrated(true);
          
          if (state?.tokenExpiry && Date.now() >= state.tokenExpiry) {
            console.log('⚠️ [恢复存储] Token 已过期，清除认证状态');
            state.accessToken = null;
            state.isAuthenticated = false;
            state.tokenExpiry = null;
            state.user = null;
            state.permissions = [];
            localStorage.removeItem('accessToken');
            localStorage.removeItem('tokenExpiry');
          } else if (state?.accessToken) {
            console.log('✅ [恢复存储] Token 有效，保持登录状态');
            localStorage.setItem('accessToken', state.accessToken);
            if (state.tokenExpiry) {
              localStorage.setItem('tokenExpiry', state.tokenExpiry.toString());
            }
          }
        }
      },
    }
  )
);

// 初始化时检查 Token 有效性
export const initAuth = () => {
  const store = useAuthStore.getState();
  
  if (typeof window !== 'undefined' && window.location.pathname === '/login') {
    return true;
  }
  
  const isValid = store.checkTokenExpiry();
  
  if (!isValid && store.isAuthenticated) {
    if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
      window.location.href = '/login';
    }
  }
  
  return isValid;
};
